import React, { useEffect, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Modal, Field, Input, Select, Empty } from "@/components/ui-kit";
import { Plus, TrendingUp, FileText, Sheet, Search } from "lucide-react";
import { toast } from "sonner";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const SKILLS = [
  { key: "shooting", label: "Tir" },
  { key: "passing", label: "Passe" },
  { key: "dribbling", label: "Dribble" },
  { key: "defense", label: "Défense" },
  { key: "physical", label: "Physique" },
  { key: "tactical", label: "Tactique" },
];
const emptyEval = { shooting: 5, passing: 5, dribbling: 5, defense: 5, physical: 5, tactical: 5, note: "", date: new Date().toISOString().slice(0, 10) };
const ADMIN_COLORS = { green: "#22c55e", orange: "#f59e0b", red: "#ef4444", blue: "#3b82f6" };

export default function Skills() {
  const { user, can } = useAuth();
  const canEdit = can("skills_manage");
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyEval);
  const [rosterSearch, setRosterSearch] = useState("");

  useEffect(() => { api.get("/members").then((r) => { setMembers(r.data); if (r.data[0]) setMemberId(r.data[0].id); }); }, []);
  const load = () => { if (memberId) api.get(`/skills/${memberId}`).then((r) => setHistory(r.data)); };
  useEffect(load, [memberId]);

  const latest = history[history.length - 1];
  const radarData = SKILLS.map((s) => ({ skill: s.label, value: latest ? latest[s.key] : 0 }));
  const lineData = history.map((h) => ({ date: h.date?.slice(5), ...SKILLS.reduce((a, s) => ({ ...a, [s.label]: h[s.key] }), {}) }));
  const avg = latest ? (SKILLS.reduce((a, s) => a + latest[s.key], 0) / SKILLS.length).toFixed(1) : "—";

  const save = async (e) => {
    e.preventDefault();
    await api.post("/skills", { member_id: memberId, ...form, ...SKILLS.reduce((a, s) => ({ ...a, [s.key]: parseInt(form[s.key]) }), {}) });
    toast.success("Évaluation enregistrée"); setOpen(false); setForm(emptyEval); load();
  };

  const COLORS = ["#22c55e", "#38bdf8", "#facc15", "#a78bfa", "#fb923c", "#f472b6"];

  const byYear = {};
  const rosterQ = rosterSearch.trim().toLowerCase();
  members
    .filter((m) => !rosterQ || `${m.first_name} ${m.last_name}`.toLowerCase().includes(rosterQ))
    .forEach((m) => { const y = m.category || "Sans année"; (byYear[y] = byYear[y] || []).push(m); });
  const years = Object.keys(byYear).sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
  years.forEach((y) => byYear[y].sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`)));

  return (
    <div>
      <PageHeader title="Compétences" subtitle="Évaluations et évolution des compétences dans le temps."
        action={canEdit && (
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="export-skills-pdf" variant="outline" onClick={() => downloadFile("/export/skills.pdf", "competences.pdf").catch(() => toast.error("Export échoué"))}><FileText size={16} /> PDF</Button>
            <Button data-testid="export-skills-xlsx" variant="outline" onClick={() => downloadFile("/export/skills.xlsx", "competences.xlsx").catch(() => toast.error("Export échoué"))}><Sheet size={16} /> Excel</Button>
            {memberId && <Button data-testid="add-eval-btn" onClick={() => setOpen(true)}><Plus size={16} /> Évaluer</Button>}
          </div>
        )} />

      <Card className="mb-6">
        <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Joueur</label>
        <Select data-testid="skills-member-select" value={memberId} onChange={(e) => setMemberId(e.target.value)}>
          {members.map((m) => <option key={m.id} value={m.id}>{m.first_name} {m.last_name} ({m.category})</option>)}
        </Select>
      </Card>

      {members.length > 0 && (
        <Card className="mb-6" data-testid="skills-roster">
          <h3 className="font-display font-bold uppercase tracking-tight mb-3">Joueurs par année de naissance</h3>
          <div className="relative mb-4">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="roster-search" value={rosterSearch} onChange={(e) => setRosterSearch(e.target.value)} placeholder="Rechercher un joueur..." className="pl-9" />
          </div>
          <div className="space-y-3">
            {years.length ? years.map((y) => (
              <div key={y} data-testid={`roster-year-${y}`}>
                <p className="text-[11px] font-black uppercase tracking-widest text-muted-foreground mb-1.5">{y} <span className="font-normal normal-case">· {byYear[y].length} joueur(s)</span></p>
                <div className="flex flex-wrap gap-1.5">
                  {byYear[y].map((m) => (
                    <button key={m.id} data-testid={`roster-player-${m.id}`} onClick={() => setMemberId(m.id)}
                      className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold transition-colors border ${memberId === m.id ? "border-primary bg-primary/10 ring-1 ring-primary" : "border-border bg-secondary/40 hover:border-primary/40"}`}
                      style={m.admin_color ? { color: ADMIN_COLORS[m.admin_color] } : undefined}>
                      {m.last_name} {m.first_name}
                    </button>
                  ))}
                </div>
              </div>
            )) : <p className="text-xs text-muted-foreground" data-testid="roster-empty">Aucun joueur trouvé.</p>}
          </div>
        </Card>
      )}

      {history.length ? (
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold uppercase tracking-tight">Profil actuel</h3>
              <div className="text-right"><p className="font-display text-3xl font-black text-primary leading-none">{avg}</p><p className="text-xs text-muted-foreground uppercase">Moyenne /10</p></div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="hsl(240 5% 20%)" />
                <PolarAngleAxis dataKey="skill" tick={{ fill: "hsl(240 5% 65%)", fontSize: 12 }} />
                <PolarRadiusAxis domain={[0, 10]} tick={{ fill: "hsl(240 5% 40%)", fontSize: 10 }} />
                <Radar dataKey="value" stroke="#22c55e" fill="#22c55e" fillOpacity={0.4} />
              </RadarChart>
            </ResponsiveContainer>
          </Card>
          <Card>
            <h3 className="font-display font-bold uppercase tracking-tight mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-primary" /> Évolution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={lineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 5% 15%)" />
                <XAxis dataKey="date" tick={{ fill: "hsl(240 5% 65%)", fontSize: 11 }} />
                <YAxis domain={[0, 10]} tick={{ fill: "hsl(240 5% 65%)", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "hsl(240 5% 8%)", border: "1px solid hsl(240 5% 20%)", borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {SKILLS.map((s, i) => <Line key={s.key} type="monotone" dataKey={s.label} stroke={COLORS[i]} strokeWidth={2} dot={false} />)}
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </div>
      ) : <Empty text="Aucune évaluation pour ce joueur." />}

      <Modal open={open} onClose={() => setOpen(false)} title="Nouvelle évaluation">
        <form onSubmit={save}>
          <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
          {SKILLS.map((s) => (
            <div key={s.key} className="mb-4">
              <div className="flex justify-between mb-1"><label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{s.label}</label><span className="text-primary font-bold text-sm">{form[s.key]}</span></div>
              <input data-testid={`skill-${s.key}`} type="range" min="0" max="10" value={form[s.key]} onChange={(e) => setForm({ ...form, [s.key]: e.target.value })} className="w-full accent-[#22c55e]" />
            </div>
          ))}
          <Field label="Note du coach"><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
          <Button data-testid="save-eval-btn" className="w-full mt-2">Enregistrer</Button>
        </form>
      </Modal>
    </div>
  );
}
